/**
 * handlers.ts - Outlinr Command Handlers
 * 
 * CHANGE HISTORY:
 * - 251115_2230 [pending] danielbeer: Create command handlers for Outlinr
 * 
 * Implements all command handlers defined in COMMAND_REGISTRY.json
 */

import type { HierarchicalSelectrManager } from '@opcentral/selectr';

export interface OutlinerContext {
  // Selection manager
  selectr: HierarchicalSelectrManager;
  
  // Navigation state
  currentNodeId: string | null;
  visibleNodes: string[];
  
  // Data operations
  getData: () => any[];
  updateData: (data: any[]) => void;
  
  // Node operations
  getNode: (id: string) => any | undefined;
  addNode: (node: any, position: 'before' | 'after' | 'child', targetId: string) => void;
  updateNode: (id: string, updates: any) => void;
  deleteNodes: (ids: string[]) => void;
  moveNode: (id: string, targetId: string, position: 'before' | 'after' | 'child') => void;
  
  // View operations
  expandNode: (id: string) => void;
  collapseNode: (id: string) => void;
  isExpanded: (id: string) => boolean;
  
  // Edit mode
  startEdit: (id: string) => void;
  cancelEdit: () => void;
  isEditing: boolean;
  mode: 'navigation' | 'edit';
  
  // Clipboard (Clippr integration)
  clippr?: any;
  
  // Undo/Redo (Backtrackr integration)
  backtrackr?: any;
  
  // Find/Filter
  showFind?: () => void;
  showFilter?: () => void;
  
  // Combine generations dialog
  showCombineDialog?: (nodeIds: string[], direction: 'up' | 'down', type: 'move' | 'generation') => void;
}

/**
 * Helper: Check if command should run based on current mode
 */
const shouldHandleCommand = (context: OutlinerContext, commandId: string): boolean => {
  if (context.mode === 'edit') {
    // Only allow specific commands in edit mode
    const editModeCommands = [
      'outliner:save-edit',
      'outliner:cancel-edit',
      'outliner:create-sibling-after',
      'outliner:create-sibling-before',
      'outliner:create-child'
    ];
    return editModeCommands.includes(commandId);
  }
  // All commands allowed in navigation mode
  return true;
};

/**
 * Create command handlers with context
 */
export const createCommandHandlers = (context: OutlinerContext) => {
  
  // ========================================
  // NAVIGATION COMMANDS
  // ========================================
  
  const handleNavigateUp = () => {
    if (!shouldHandleCommand(context, 'outliner:navigate-up')) return;
    
    const { currentNodeId, visibleNodes } = context;
    
    // CASE 1: No selection - select last item
    if (!currentNodeId && visibleNodes.length > 0) {
      const lastId = visibleNodes[visibleNodes.length - 1];
      context.selectr.select(lastId, 'replace');
      // TODO: Scroll into view
      return;
    }
    
    // CASE 2: Has selection - navigate to previous
    if (!currentNodeId) return;
    
    const currentIndex = visibleNodes.indexOf(currentNodeId);
    if (currentIndex > 0) {
      const previousId = visibleNodes[currentIndex - 1];
      context.selectr.select(previousId, 'replace');
      // TODO: Scroll into view
    }
  };
  
  const handleNavigateDown = () => {
    if (!shouldHandleCommand(context, 'outliner:navigate-down')) return;
    
    const { currentNodeId, visibleNodes } = context;
    
    // CASE 1: No selection - select first item
    if (!currentNodeId && visibleNodes.length > 0) {
      const firstId = visibleNodes[0];
      context.selectr.select(firstId, 'replace');
      // TODO: Scroll into view
      return;
    }
    
    // CASE 2: Has selection - navigate to next
    if (!currentNodeId) return;
    
    const currentIndex = visibleNodes.indexOf(currentNodeId);
    if (currentIndex < visibleNodes.length - 1) {
      const nextId = visibleNodes[currentIndex + 1];
      context.selectr.select(nextId, 'replace');
      // TODO: Scroll into view
    }
  };
  
  const handleNavigateLeft = () => {
    if (!shouldHandleCommand(context, 'outliner:navigate-left')) return;
    
    const { currentNodeId } = context;
    if (!currentNodeId) return;
    
    const node = context.getNode(currentNodeId);
    if (!node) return;
    
    // If expanded, collapse
    if (context.isExpanded(currentNodeId)) {
      context.collapseNode(currentNodeId);
    }
    // Otherwise navigate to parent
    else if (node.parentId) {
      context.selectr.select(node.parentId, 'replace');
    }
  };
  
  const handleNavigateRight = () => {
    if (!shouldHandleCommand(context, 'outliner:navigate-right')) return;
    
    const { currentNodeId } = context;
    if (!currentNodeId) return;
    
    const node = context.getNode(currentNodeId);
    if (!node) return;
    
    // If collapsed, expand
    if (!context.isExpanded(currentNodeId)) {
      context.expandNode(currentNodeId);
    }
    // Otherwise navigate to first child
    else {
      const children = context.selectr.getNode(currentNodeId)?.children;
      if (children && children.length > 0) {
        context.selectr.select(children[0], 'replace');
      }
    }
  };
  
  const handleNavigateHome = () => {
    if (!shouldHandleCommand(context, 'outliner:navigate-home')) return;
    
    const { visibleNodes } = context;
    if (visibleNodes.length > 0) {
      context.selectr.select(visibleNodes[0], 'replace');
    }
  };
  
  const handleNavigateEnd = () => {
    if (!shouldHandleCommand(context, 'outliner:navigate-end')) return;
    
    const { visibleNodes } = context;
    if (visibleNodes.length > 0) {
      context.selectr.select(visibleNodes[visibleNodes.length - 1], 'replace');
    }
  };
  
  const handleNavigatePageUp = () => {
    if (!shouldHandleCommand(context, 'outliner:navigate-page-up')) return;
    
    // TODO: Implement page-based navigation
    // For now, jump 10 nodes up
    const { currentNodeId, visibleNodes } = context;
    if (!currentNodeId) return;
    
    const currentIndex = visibleNodes.indexOf(currentNodeId);
    const targetIndex = Math.max(0, currentIndex - 10);
    context.selectr.select(visibleNodes[targetIndex], 'replace');
  };
  
  const handleNavigatePageDown = () => {
    if (!shouldHandleCommand(context, 'outliner:navigate-page-down')) return;
    
    // TODO: Implement page-based navigation
    // For now, jump 10 nodes down
    const { currentNodeId, visibleNodes } = context;
    if (!currentNodeId) return;
    
    const currentIndex = visibleNodes.indexOf(currentNodeId);
    const targetIndex = Math.min(visibleNodes.length - 1, currentIndex + 10);
    context.selectr.select(visibleNodes[targetIndex], 'replace');
  };
  
  // ========================================
  // SELECTION COMMANDS
  // ========================================
  
  const handleSelectUp = () => {
    // Disabled - handled by keyboard handler in keyboardHandlers.ts
    // The keyboard handler has better logic for tracking active end of selection
    return;
  };
  
  const handleSelectDown = () => {
    // Disabled - handled by keyboard handler in keyboardHandlers.ts
    // The keyboard handler has better logic for tracking active end of selection
    return;
  };
  
  const handleSelectAll = () => {
    if (!shouldHandleCommand(context, 'outliner:select-all')) return;
    
    context.selectr.selectAll(context.visibleNodes);
  };
  
  const handleSelectNone = () => {
    if (!shouldHandleCommand(context, 'outliner:select-none')) return;
    
    context.selectr.clearSelection();
  };
  
  const handleSelectChildren = () => {
    if (!shouldHandleCommand(context, 'outliner:select-children')) return;
    
    const { currentNodeId } = context;
    if (!currentNodeId) return;
    
    context.selectr.selectChildren(currentNodeId, false);
  };
  
  const handleSelectSiblings = () => {
    if (!shouldHandleCommand(context, 'outliner:select-siblings')) return;
    
    const { currentNodeId } = context;
    if (!currentNodeId) return;
    
    context.selectr.selectSiblings(currentNodeId, true);
  };
  
  const handleSelectDescendants = () => {
    if (!shouldHandleCommand(context, 'outliner:select-descendants')) return;
    
    const { currentNodeId } = context;
    if (!currentNodeId) return;
    
    context.selectr.selectDescendants(currentNodeId);
  };
  
  const handleToggleSelection = () => {
    if (!shouldHandleCommand(context, 'outliner:toggle-selection')) return;
    
    const { currentNodeId } = context;
    if (!currentNodeId) return;
    
    context.selectr.select(currentNodeId, 'toggle');
  };
  
  // ========================================
  // EDITING COMMANDS
  // ========================================
  
  const handleAddAfter = () => {
    if (!shouldHandleCommand(context, 'outliner:add-after')) return;
    
    const { currentNodeId, visibleNodes } = context;
    if (context.isEditing) return;
    
    // CASE 1: No selection - add at bottom
    if (!currentNodeId || visibleNodes.length === 0) {
      const newNode = {
        id: `node-${Date.now()}`,
        text: '',
        parentId: null,
        generation: 0
      };
      
      // Add at end (after last visible node)
      const lastNodeId = visibleNodes.length > 0 ? visibleNodes[visibleNodes.length - 1] : '';
      context.addNode(newNode, 'after', lastNodeId || '');
      context.selectr.select(newNode.id, 'replace');
      context.startEdit(newNode.id);
      return;
    }
    
    // CASE 2: Has selection - add sibling after (existing behavior)
    const newNode = {
      id: `node-${Date.now()}`,
      text: '',
      parentId: context.getNode(currentNodeId)?.parentId || null,
      generation: context.getNode(currentNodeId)?.generation || 0
    };
    
    context.addNode(newNode, 'after', currentNodeId);
    context.selectr.select(newNode.id, 'replace');
    context.startEdit(newNode.id);
  };
  
  const handleAddBefore = () => {
    if (!shouldHandleCommand(context, 'outliner:add-before')) return;
    
    const { currentNodeId } = context;
    if (!currentNodeId || context.isEditing) return;
    
    const newNode = {
      id: `node-${Date.now()}`,
      text: '',
      parentId: context.getNode(currentNodeId)?.parentId || null,
      generation: context.getNode(currentNodeId)?.generation || 0
    };
    
    context.addNode(newNode, 'before', currentNodeId);
    context.selectr.select(newNode.id, 'replace');
    context.startEdit(newNode.id);
  };
  
  const handleAddChild = () => {
    if (!shouldHandleCommand(context, 'outliner:add-child')) return;
    
    const { currentNodeId } = context;
    if (!currentNodeId || context.isEditing) return;
    
    const currentNode = context.getNode(currentNodeId);
    if (!currentNode) return;
    
    const newNode = {
      id: `node-${Date.now()}`,
      text: '',
      parentId: currentNodeId,
      generation: currentNode.generation + 1
    };
    
    context.addNode(newNode, 'child', currentNodeId);
    context.expandNode(currentNodeId); // Expand parent
    context.selectr.select(newNode.id, 'replace');
    context.startEdit(newNode.id);
  };
  
  const handleEdit = () => {
    if (!shouldHandleCommand(context, 'outliner:edit')) return;
    
    const { currentNodeId } = context;
    if (!currentNodeId || context.isEditing) return;
    
    context.startEdit(currentNodeId);
  };
  
  const handleCancelEdit = () => {
    if (!shouldHandleCommand(context, 'outliner:cancel-edit')) return;
    
    if (context.isEditing) {
      context.cancelEdit();
    }
  };
  
  const handleDelete = () => {
    console.log('[handleDelete] Called');
    if (!shouldHandleCommand(context, 'outliner:delete')) return;
    
    if (context.isEditing) return;
    
    const selectedIds = context.selectr.getSelected();
    console.log('[handleDelete] Selected IDs:', selectedIds);
    if (selectedIds.length === 0) return;
    
    // Validate deletion (family rules, etc.)
    const validation = context.selectr.validateSelection(selectedIds);
    if (!validation.valid) {
      console.warn('Cannot delete: ', validation.reason);
      return;
    }
    
    // Find the node to select after deletion (previous visible node)
    const firstDeletedIndex = Math.min(...selectedIds.map(id => 
      context.visibleNodes.indexOf(id)
    ).filter(idx => idx !== -1));
    
    let nextSelection: string | null = null;
    if (firstDeletedIndex > 0) {
      // Select the previous visible node
      nextSelection = context.visibleNodes[firstDeletedIndex - 1];
    } else if (context.visibleNodes.length > selectedIds.length) {
      // If deleting the first node(s), select the first remaining node
      nextSelection = context.visibleNodes.find(id => !selectedIds.includes(id)) || null;
    }
    
    // TODO: Confirm deletion if multiple nodes
    console.log('[handleDelete] Deleting nodes:', selectedIds);
    context.deleteNodes(selectedIds);
    
    // Select the next node after deletion
    if (nextSelection) {
      context.selectr.select(nextSelection, 'replace');
    } else {
      context.selectr.clearSelection();
    }
  };
  
  const handleDuplicate = () => {
    if (!shouldHandleCommand(context, 'outliner:duplicate')) return;
    
    if (context.isEditing) return;
    
    const selectedIds = context.selectr.getSelected();
    if (selectedIds.length === 0) return;
    
    // TODO: Implement duplication with structure preservation
    console.log('Duplicate:', selectedIds);
  };
  
  // ========================================
  // MOVEMENT COMMANDS
  // ========================================
  
  const handleMoveUp = () => {
    if (!shouldHandleCommand(context, 'outliner:move-up')) return;
    
    if (context.isEditing) return;
    
    const selectedIds = context.selectr.getSelected();
    if (selectedIds.length === 0) return;
    
    // Get all visible nodes to understand visual order
    const allData = context.getData();
    const visibleNodes = context.visibleNodes;
    
    // Sort selected nodes by their visual order
    const sortedNodeIds = selectedIds.slice().sort((a, b) => {
      const indexA = visibleNodes.findIndex(n => n === a);
      const indexB = visibleNodes.findIndex(n => n === b);
      return indexA - indexB;
    });
    
    // Check if all selected nodes share the same parent
    const firstNode = context.getNode(sortedNodeIds[0]);
    if (!firstNode) return;
    
    const allSameParent = sortedNodeIds.every(id => {
      const node = context.getNode(id);
      return node && node.parentId === firstNode.parentId;
    });
    
    if (!allSameParent) {
      // Show combine dialog
      if (context.showCombineDialog) {
        context.showCombineDialog(sortedNodeIds, 'up', 'move');
      }
      return;
    }
    
    // All have same parent - proceed with move
    const parentId = firstNode.parentId;
    const siblings = allData.filter(n => n.parentId === parentId);
    
    // Get indices of selected nodes within their siblings
    const selectedIndices = sortedNodeIds.map(id => siblings.findIndex(s => s.id === id));
    const minIndex = Math.min(...selectedIndices);
    
    if (minIndex === 0) {
      // At top of generation - outdent and position at bottom of parent's siblings
      if (!parentId) return; // Already at root
      
      const parent = context.getNode(parentId);
      if (!parent) return;
      
      const grandparentId = parent.parentId;
      const parentSiblings = allData.filter(n => n.parentId === grandparentId);
      const parentIndex = parentSiblings.findIndex(s => s.id === parentId);
      
      // Helper to get all descendants
      const getDescendants = (nodeId: string, nodes: any[]): string[] => {
        const children = nodes.filter(n => n.parentId === nodeId);
        const descendants = [...children.map(c => c.id)];
        children.forEach(child => {
          descendants.push(...getDescendants(child.id, nodes));
        });
        return descendants;
      };
      
      // Get all affected nodes (selected + descendants)
      const allAffectedIds = new Set(sortedNodeIds);
      sortedNodeIds.forEach(id => {
        getDescendants(id, allData).forEach(descId => allAffectedIds.add(descId));
      });
      
      // Calculate generation delta
      const generationDelta = (parent.generation || 0) - (firstNode.generation || 0);
      
      // Update nodes to be siblings of parent
      let newData = allData.map(n => {
        if (sortedNodeIds.includes(n.id)) {
          return {
            ...n,
            parentId: grandparentId,
            generation: (n.generation || 0) + generationDelta
          };
        } else if (allAffectedIds.has(n.id)) {
          return {
            ...n,
            generation: (n.generation || 0) + generationDelta
          };
        }
        return n;
      });
      
      // Reorder: move selected nodes to position right before parent
      const selectedNodes = newData.filter(n => sortedNodeIds.includes(n.id));
      const otherNodes = newData.filter(n => !sortedNodeIds.includes(n.id));
      const parentNodeIndex = otherNodes.findIndex(n => n.id === parentId);
      
      if (parentNodeIndex !== -1) {
        newData = [
          ...otherNodes.slice(0, parentNodeIndex),
          ...selectedNodes,
          ...otherNodes.slice(parentNodeIndex)
        ];
      }
      
      context.updateData(newData);
      return;
    }
    
    // Get the sibling immediately above
    const targetSibling = siblings[minIndex - 1];
    if (!targetSibling) return;
    
    // Check if target sibling is expanded and has children
    const targetNode = context.getNode(targetSibling.id);
    const isExpanded = targetNode?.expanded;
    const hasChildren = allData.some(n => n.parentId === targetSibling.id);
    
    let newData = [...allData];
    
    if (isExpanded && hasChildren) {
      // Smart move: Move INTO the expanded sibling (as last child)
      const targetChildren = allData.filter(n => n.parentId === targetSibling.id);
      const lastChildIndex = allData.findIndex(n => n.id === targetChildren[targetChildren.length - 1]?.id);
      
      // Update selected nodes to be children of target sibling
      newData = newData.map(n => {
        if (sortedNodeIds.includes(n.id)) {
          return {
            ...n,
            parentId: targetSibling.id,
            generation: (targetNode?.generation || 0) + 1
          };
        }
        return n;
      });
      
      // Reorder: move selected items after last child
      const selectedNodes = newData.filter(n => sortedNodeIds.includes(n.id));
      const otherNodes = newData.filter(n => !sortedNodeIds.includes(n.id));
      
      // Insert selected nodes after last child
      if (lastChildIndex !== -1) {
        newData = [
          ...otherNodes.slice(0, lastChildIndex + 1),
          ...selectedNodes,
          ...otherNodes.slice(lastChildIndex + 1)
        ];
      }
    } else {
      // Normal move: Move entire block up by swapping with the one sibling above
      const selectedNodes = newData.filter(n => sortedNodeIds.includes(n.id));
      const otherNodes = newData.filter(n => !sortedNodeIds.includes(n.id));
      
      // Find the sibling immediately before the block
      const targetSiblingIndex = otherNodes.findIndex(n => n.id === targetSibling.id);
      
      if (targetSiblingIndex !== -1) {
        // Insert selected block before the target sibling
        newData = [
          ...otherNodes.slice(0, targetSiblingIndex),
          ...selectedNodes,
          ...otherNodes.slice(targetSiblingIndex)
        ];
      }
    }
    
    context.updateData(newData);
  };
  
  const handleMoveDown = () => {
    if (!shouldHandleCommand(context, 'outliner:move-down')) return;
    
    if (context.isEditing) return;
    
    const selectedIds = context.selectr.getSelected();
    if (selectedIds.length === 0) return;
    
    // Get all visible nodes to understand visual order
    const allData = context.getData();
    const visibleNodes = context.visibleNodes;
    
    // Sort selected nodes by their visual order
    const sortedNodeIds = selectedIds.slice().sort((a, b) => {
      const indexA = visibleNodes.findIndex(n => n === a);
      const indexB = visibleNodes.findIndex(n => n === b);
      return indexA - indexB;
    });
    
    // Check if all selected nodes share the same parent
    const firstNode = context.getNode(sortedNodeIds[0]);
    if (!firstNode) return;
    
    const allSameParent = sortedNodeIds.every(id => {
      const node = context.getNode(id);
      return node && node.parentId === firstNode.parentId;
    });
    
    if (!allSameParent) {
      // Show combine dialog
      if (context.showCombineDialog) {
        context.showCombineDialog(sortedNodeIds, 'down', 'move');
      }
      return;
    }
    
    // All have same parent - proceed with move
    const parentId = firstNode.parentId;
    const siblings = allData.filter(n => n.parentId === parentId);
    
    // Get indices of selected nodes within their siblings
    const selectedIndices = sortedNodeIds.map(id => siblings.findIndex(s => s.id === id));
    const maxIndex = Math.max(...selectedIndices);
    
    if (maxIndex >= siblings.length - 1) {
      // At bottom of generation - outdent and position at top of parent's next siblings
      if (!parentId) return; // Already at root
      
      const parent = context.getNode(parentId);
      if (!parent) return;
      
      const grandparentId = parent.parentId;
      const parentSiblings = allData.filter(n => n.parentId === grandparentId);
      const parentIndex = parentSiblings.findIndex(s => s.id === parentId);
      
      // Helper to get all descendants
      const getDescendants = (nodeId: string, nodes: any[]): string[] => {
        const children = nodes.filter(n => n.parentId === nodeId);
        const descendants = [...children.map(c => c.id)];
        children.forEach(child => {
          descendants.push(...getDescendants(child.id, nodes));
        });
        return descendants;
      };
      
      // Get all affected nodes (selected + descendants)
      const allAffectedIds = new Set(sortedNodeIds);
      sortedNodeIds.forEach(id => {
        getDescendants(id, allData).forEach(descId => allAffectedIds.add(descId));
      });
      
      // Calculate generation delta
      const generationDelta = (parent.generation || 0) - (firstNode.generation || 0);
      
      // Update nodes to be siblings of parent
      let newData = allData.map(n => {
        if (sortedNodeIds.includes(n.id)) {
          return {
            ...n,
            parentId: grandparentId,
            generation: (n.generation || 0) + generationDelta
          };
        } else if (allAffectedIds.has(n.id)) {
          return {
            ...n,
            generation: (n.generation || 0) + generationDelta
          };
        }
        return n;
      });
      
      // Reorder: move selected nodes to position right after parent (and all its descendants)
      const selectedNodes = newData.filter(n => sortedNodeIds.includes(n.id));
      const otherNodes = newData.filter(n => !sortedNodeIds.includes(n.id));
      
      // Find where parent ends (after all its descendants)
      const parentDescendants = getDescendants(parentId, otherNodes);
      let insertIndex = otherNodes.findIndex(n => n.id === parentId) + 1;
      
      // Skip past all descendants of parent
      while (insertIndex < otherNodes.length && parentDescendants.includes(otherNodes[insertIndex].id)) {
        insertIndex++;
      }
      
      newData = [
        ...otherNodes.slice(0, insertIndex),
        ...selectedNodes,
        ...otherNodes.slice(insertIndex)
      ];
      
      context.updateData(newData);
      return;
    }
    
    // Get the sibling immediately below
    const targetSibling = siblings[maxIndex + 1];
    if (!targetSibling) return;
    
    // Check if target sibling is expanded and has children
    const targetNode = context.getNode(targetSibling.id);
    const isExpanded = targetNode?.expanded;
    const hasChildren = allData.some(n => n.parentId === targetSibling.id);
    
    let newData = [...allData];
    
    if (isExpanded && hasChildren) {
      // Smart move: Move INTO the expanded sibling (as first child)
      const targetChildren = allData.filter(n => n.parentId === targetSibling.id);
      const firstChildIndex = allData.findIndex(n => n.id === targetChildren[0]?.id);
      
      // Update selected nodes to be children of target sibling
      newData = newData.map(n => {
        if (sortedNodeIds.includes(n.id)) {
          return {
            ...n,
            parentId: targetSibling.id,
            generation: (targetNode?.generation || 0) + 1
          };
        }
        return n;
      });
      
      // Reorder: move selected items before first child
      const selectedNodes = newData.filter(n => sortedNodeIds.includes(n.id));
      const otherNodes = newData.filter(n => !sortedNodeIds.includes(n.id));
      
      // Insert selected nodes before first child
      if (firstChildIndex !== -1) {
        newData = [
          ...otherNodes.slice(0, firstChildIndex),
          ...selectedNodes,
          ...otherNodes.slice(firstChildIndex)
        ];
      }
    } else {
      // Normal move: Swap positions with sibling below
      sortedNodeIds.slice().reverse().forEach((nodeId, idx) => {
        const nodeIndex = newData.findIndex(n => n.id === nodeId);
        const targetIdx = newData.findIndex(n => n.id === siblings[maxIndex + 1]?.id);
        if (nodeIndex !== -1 && targetIdx !== -1) {
          [newData[nodeIndex], newData[targetIdx]] = [newData[targetIdx], newData[nodeIndex]];
        }
      });
    }
    
    context.updateData(newData);
  };
  
  const handleIndent = () => {
    if (!shouldHandleCommand(context, 'outliner:indent')) return;
    
    if (context.isEditing) return;
    
    const selectedIds = context.selectr.getSelected();
    if (selectedIds.length === 0) return;
    
    const allData = context.getData();
    const visibleNodes = context.visibleNodes;
    
    // Sort selected nodes by their visual order
    const sortedNodeIds = selectedIds.slice().sort((a, b) => {
      const indexA = visibleNodes.findIndex(n => n === a);
      const indexB = visibleNodes.findIndex(n => n === b);
      return indexA - indexB;
    });
    
    // Check if all selected nodes share the same parent
    const firstNode = context.getNode(sortedNodeIds[0]);
    if (!firstNode) return;
    
    const allSameParent = sortedNodeIds.every(id => {
      const node = context.getNode(id);
      return node && node.parentId === firstNode.parentId;
    });
    
    if (!allSameParent) {
      // Show combine dialog
      if (context.showCombineDialog) {
        context.showCombineDialog(sortedNodeIds, 'down', 'generation');
      }
      return;
    }
    
    // Get siblings
    const siblings = allData.filter(n => n.parentId === firstNode.parentId);
    const firstIndex = siblings.findIndex(s => s.id === sortedNodeIds[0]);
    
    // Must have a previous sibling to indent under
    if (firstIndex <= 0) {
      console.warn('Cannot indent: no previous sibling');
      return;
    }
    
    const previousSibling = siblings[firstIndex - 1];
    
    // Helper to get all descendants of a node
    const getDescendants = (nodeId: string, nodes: any[]): string[] => {
      const children = nodes.filter(n => n.parentId === nodeId);
      const descendants = [...children.map(c => c.id)];
      children.forEach(child => {
        descendants.push(...getDescendants(child.id, nodes));
      });
      return descendants;
    };
    
    // Get all descendants of selected nodes
    const allAffectedIds = new Set(sortedNodeIds);
    sortedNodeIds.forEach(id => {
      getDescendants(id, allData).forEach(descId => allAffectedIds.add(descId));
    });
    
    // Calculate generation delta (how much to increase generation by)
    const generationDelta = (previousSibling.generation || 0) + 1 - (firstNode.generation || 0);
    
    // Update all selected nodes and their descendants
    const newData = allData.map(n => {
      if (sortedNodeIds.includes(n.id)) {
        // Selected nodes: change parent and adjust generation
        return {
          ...n,
          parentId: previousSibling.id,
          generation: (n.generation || 0) + generationDelta
        };
      } else if (allAffectedIds.has(n.id)) {
        // Descendants: only adjust generation
        return {
          ...n,
          generation: (n.generation || 0) + generationDelta
        };
      }
      return n;
    });
    
    context.updateData(newData);
    
    // Expand the new parent
    context.expandNode(previousSibling.id);
  };
  
  const handleOutdent = () => {
    if (!shouldHandleCommand(context, 'outliner:outdent')) return;
    
    if (context.isEditing) return;
    
    const selectedIds = context.selectr.getSelected();
    if (selectedIds.length === 0) return;
    
    const allData = context.getData();
    const visibleNodes = context.visibleNodes;
    
    // Sort selected nodes by their visual order
    const sortedNodeIds = selectedIds.slice().sort((a, b) => {
      const indexA = visibleNodes.findIndex(n => n === a);
      const indexB = visibleNodes.findIndex(n => n === b);
      return indexA - indexB;
    });
    
    // Check if all selected nodes share the same parent
    const firstNode = context.getNode(sortedNodeIds[0]);
    if (!firstNode) return;
    
    const allSameParent = sortedNodeIds.every(id => {
      const node = context.getNode(id);
      return node && node.parentId === firstNode.parentId;
    });
    
    if (!allSameParent) {
      // Show combine dialog
      if (context.showCombineDialog) {
        context.showCombineDialog(sortedNodeIds, 'up', 'generation');
      }
      return;
    }
    
    // Must have a parent to outdent
    if (!firstNode.parentId) {
      console.warn('Cannot outdent: already at root level');
      return;
    }
    
    const parent = context.getNode(firstNode.parentId);
    if (!parent) return;
    
    // Helper to get all descendants of a node
    const getDescendants = (nodeId: string, nodes: any[]): string[] => {
      const children = nodes.filter(n => n.parentId === nodeId);
      const descendants = [...children.map(c => c.id)];
      children.forEach(child => {
        descendants.push(...getDescendants(child.id, nodes));
      });
      return descendants;
    };
    
    // Get all descendants of selected nodes
    const allAffectedIds = new Set(sortedNodeIds);
    sortedNodeIds.forEach(id => {
      getDescendants(id, allData).forEach(descId => allAffectedIds.add(descId));
    });
    
    // Calculate generation delta (how much to decrease generation by)
    const generationDelta = (parent.generation || 0) - (firstNode.generation || 0);
    
    // Update all selected nodes and their descendants
    const newData = allData.map(n => {
      if (sortedNodeIds.includes(n.id)) {
        // Selected nodes: change parent and adjust generation
        return {
          ...n,
          parentId: parent.parentId,
          generation: (n.generation || 0) + generationDelta
        };
      } else if (allAffectedIds.has(n.id)) {
        // Descendants: only adjust generation
        return {
          ...n,
          generation: (n.generation || 0) + generationDelta
        };
      }
      return n;
    });
    
    context.updateData(newData);
  };
  
  const handleMoveToParent = () => {
    if (!shouldHandleCommand(context, 'outliner:move-to-parent')) return;
    
    if (context.isEditing) return;
    
    const selectedIds = context.selectr.getSelected();
    if (selectedIds.length === 0) return;
    
    // TODO: Show parent picker dialog
    console.log('Move to parent:', selectedIds);
  };
  
  // ========================================
  // CLIPBOARD COMMANDS
  // ========================================
  
  const handleCopy = async (format: string = 'tab-indented') => {
    if (context.isEditing) return;
    
    console.log('[handleCopy] Format:', format);
    
    const selectedIds = context.selectr.getSelected();
    if (selectedIds.length === 0) return;
    
    const selectedNodes = context.selectr.getSelectedNodes();
    const allData = context.getData();
    
    // Get all descendants of selected nodes
    const getAllDescendants = (nodeId: string): any[] => {
      const children = allData.filter(n => n.parentId === nodeId);
      const descendants = [...children];
      children.forEach(child => {
        descendants.push(...getAllDescendants(child.id));
      });
      return descendants;
    };
    
    // Build complete structure with descendants
    const nodesToCopy = new Set<string>(selectedIds);
    selectedIds.forEach(id => {
      getAllDescendants(id).forEach(desc => nodesToCopy.add(desc.id));
    });
    
    const structuredData = allData.filter(n => nodesToCopy.has(n.id));
    
    // Build text in requested format
    const buildText = (nodeId: string, depth: number = 0): string => {
      const node = allData.find(n => n.id === nodeId);
      if (!node) return '';
      
      let indent = '';
      let prefix = '';
      
      switch (format) {
        case 'tab-indented':
          indent = '\t'.repeat(depth);
          break;
        case 'space-indented':
          indent = '  '.repeat(depth);
          break;
        case 'markdown':
          indent = '  '.repeat(depth);
          prefix = '- ';
          break;
        case 'numbered':
          indent = '  '.repeat(depth);
          prefix = `${depth + 1}. `;
          break;
        case 'outline':
          const bullets = ['•', '◦', '▪', '▫'];
          prefix = bullets[depth % bullets.length] + ' ';
          indent = '  '.repeat(depth);
          break;
        case 'json':
          // JSON format handled separately below
          break;
      }
      
      let text = `${indent}${prefix}${node.text}\n`;
      
      const children = allData.filter(n => n.parentId === nodeId);
      children.forEach(child => {
        text += buildText(child.id, depth + 1);
      });
      
      return text;
    };
    
    let text: string;
    
    if (format === 'json') {
      // JSON format: output structured data
      text = JSON.stringify(structuredData, null, 2);
      console.log('[handleCopy] JSON output length:', text.length);
    } else {
      // Text formats: build indented text
      text = selectedIds.map(id => buildText(id)).join('');
      console.log('[handleCopy] Text output length:', text.length, 'first 100 chars:', text.substring(0, 100));
    }
    
    if (context.clippr) {
      // Use Clippr for structure-aware copy with both formats
      await context.clippr.copy({
        type: 'outliner-nodes',
        nodes: structuredData,
        rootIds: selectedIds,
        text: text,
        format: format
      });
    } else {
      // Fallback to system clipboard
      await navigator.clipboard.writeText(text);
    }
  };
  
  const handleCopyAsTabIndented = async () => await handleCopy('tab-indented');
  const handleCopyAsSpaceIndented = async () => await handleCopy('space-indented');
  const handleCopyAsMarkdown = async () => await handleCopy('markdown');
  const handleCopyAsNumbered = async () => await handleCopy('numbered');
  const handleCopyAsOutline = async () => await handleCopy('outline');
  const handleCopyAsJson = async () => await handleCopy('json');
  
  const handleCut = async () => {
    if (context.isEditing) return;
    
    await handleCopy();
    
    const selectedIds = context.selectr.getSelected();
    context.deleteNodes(selectedIds);
  };
  
  const handlePaste = async () => {
    console.log('[handlePaste] Called, isEditing:', context.isEditing);
    if (context.isEditing) return;
    
    const { currentNodeId } = context;
    console.log('[handlePaste] currentNodeId:', currentNodeId);
    if (!currentNodeId) return;
    
    const currentNode = context.getNode(currentNodeId);
    console.log('[handlePaste] currentNode:', currentNode);
    if (!currentNode) return;
    
    // Try clippr first for structured data
    let handled = false;
    if (context.clippr) {
      const clipboardData = context.clippr.paste();
      
      if (clipboardData?.type === 'outliner-nodes') {
        const { nodes: copiedNodes, rootIds } = clipboardData;
        
        // Generate new IDs for pasted nodes
        const idMap = new Map<string, string>();
        copiedNodes.forEach((node: any) => {
          idMap.set(node.id, `node-${Date.now()}-${Math.random()}`);
        });
        
        // Remap nodes with new IDs and set as children of current node
        const newNodes = copiedNodes.map((node: any) => {
          const newId = idMap.get(node.id)!;
          const isRoot = rootIds.includes(node.id);
          
          return {
            ...node,
            id: newId,
            parentId: isRoot ? currentNodeId : (idMap.get(node.parentId) || currentNodeId),
            generation: isRoot ? (currentNode.generation || 0) + 1 : node.generation
          };
        });
        
        // Append as children
        const allData = context.getData();
        const newData = [...allData, ...newNodes];
        
        context.updateData(newData);
        
        // Expand current node and select pasted roots (so user can outdent if needed)
        context.expandNode(currentNodeId);
        const newRootIds = rootIds.map((id: string) => idMap.get(id)!);
        context.selectr.select(newRootIds, 'replace');
        handled = true;
      }
    }
    
    // Fallback: parse text and create child nodes
    if (!handled) {
      const text = await navigator.clipboard.readText();
      console.log('[handlePaste] Clipboard text:', text);
      const lines = text.split('\n').filter(l => l.trim());
      console.log('[handlePaste] Parsed lines:', lines);
      
      if (lines.length === 0) {
        console.log('[handlePaste] No lines to paste');
        return;
      }
      
      const allData = context.getData();
      
      const newNodes = lines.map((line, i) => ({
        id: `node-${Date.now()}-${i}`,
        text: line.trim(),
        parentId: currentNodeId,
        generation: (currentNode.generation || 0) + 1,
        familyId: currentNode.familyId
      }));
      
      console.log('[handlePaste] Creating nodes:', newNodes);
      const newData = [...allData, ...newNodes];
      
      context.updateData(newData);
      
      // Expand current node and select pasted nodes
      context.expandNode(currentNodeId);
      const newNodeIds = newNodes.map(n => n.id);
      console.log('[handlePaste] Selecting nodes:', newNodeIds);
      context.selectr.select(newNodeIds, 'replace');
    }
  };
  
  const handlePasteAsChild = async () => {
    if (context.isEditing) return;
    
    const { currentNodeId } = context;
    if (!currentNodeId) return;
    
    const currentNode = context.getNode(currentNodeId);
    if (!currentNode) return;
    
    if (context.clippr) {
      const clipboardData = context.clippr.paste();
      
      if (clipboardData?.type === 'outliner-nodes') {
        const { nodes: copiedNodes, rootIds } = clipboardData;
        
        // Generate new IDs for pasted nodes
        const idMap = new Map<string, string>();
        copiedNodes.forEach((node: any) => {
          idMap.set(node.id, `node-${Date.now()}-${Math.random()}`);
        });
        
        // Remap nodes with new IDs and set as children of current
        const newNodes = copiedNodes.map((node: any) => {
          const newId = idMap.get(node.id)!;
          const isRoot = rootIds.includes(node.id);
          
          return {
            ...node,
            id: newId,
            parentId: isRoot ? currentNodeId : (idMap.get(node.parentId) || currentNodeId),
            generation: isRoot ? currentNode.generation + 1 : node.generation
          };
        });
        
        // Append to data
        const allData = context.getData();
        const newData = [...allData, ...newNodes];
        
        context.updateData(newData);
        
        // Expand current node and select pasted roots
        context.expandNode(currentNodeId);
        const newRootIds = rootIds.map((id: string) => idMap.get(id)!);
        context.selectr.select(newRootIds, 'replace');
      }
    } else {
      // Fallback: parse text and create child nodes
      const text = await navigator.clipboard.readText();
      const lines = text.split('\n').filter(l => l.trim());
      
      const allData = context.getData();
      
      const newNodes = lines.map((line, i) => ({
        id: `node-${Date.now()}-${i}`,
        text: line.trim(),
        parentId: currentNodeId,
        generation: currentNode.generation + 1,
        familyId: currentNode.familyId
      }));
      
      const newData = [...allData, ...newNodes];
      
      context.updateData(newData);
      context.expandNode(currentNodeId);
    }
  };
  
  // ========================================
  // UNDO/REDO COMMANDS
  // ========================================
  
  const handleUndo = async () => {
    if (context.backtrackr) {
      await context.backtrackr.undo();
    } else {
      console.warn('[Outlinr] Backtrackr not available for undo');
    }
  };
  
  const handleRedo = async () => {
    if (context.backtrackr) {
      await context.backtrackr.redo();
    } else {
      console.warn('[Outlinr] Backtrackr not available for redo');
    }
  };
  
  // ========================================
  // VIEW COMMANDS
  // ========================================
  
  const handleExpand = () => {
    if (!shouldHandleCommand(context, 'outliner:expand')) return;
    
    const { currentNodeId } = context;
    if (!currentNodeId) return;
    
    context.expandNode(currentNodeId);
  };
  
  const handleCollapse = () => {
    if (!shouldHandleCommand(context, 'outliner:collapse')) return;
    
    const { currentNodeId } = context;
    if (!currentNodeId) return;
    
    context.collapseNode(currentNodeId);
  };
  
  const handleToggleExpand = () => {
    if (!shouldHandleCommand(context, 'outliner:toggle-expand')) return;
    
    const { currentNodeId } = context;
    if (!currentNodeId) return;
    
    if (context.isExpanded(currentNodeId)) {
      context.collapseNode(currentNodeId);
    } else {
      context.expandNode(currentNodeId);
    }
  };
  
  const handleExpandAll = () => {
    if (!shouldHandleCommand(context, 'outliner:expand-all')) return;
    
    const allNodes = context.getData();
    allNodes.forEach(node => {
      context.expandNode(node.id);
    });
  };
  
  const handleCollapseAll = () => {
    if (!shouldHandleCommand(context, 'outliner:collapse-all')) return;
    
    const allNodes = context.getData();
    allNodes.forEach(node => {
      context.collapseNode(node.id);
    });
  };
  
  const handleExpandGeneration = (generation: number) => {
    const allNodes = context.getData();
    allNodes.forEach(node => {
      if (node.generation < generation) {
        context.expandNode(node.id);
      } else if (node.generation === generation) {
        context.collapseNode(node.id);
      }
    });
  };
  
  // ========================================
  // SEARCH COMMANDS
  // ========================================
  
  const handleFind = () => {
    if (!shouldHandleCommand(context, 'outliner:find')) return;
    
    if (context.showFind) {
      context.showFind();
    } else {
      console.warn('Find not implemented');
    }
  };
  
  const handleFindNext = () => {
    if (!shouldHandleCommand(context, 'outliner:find-next')) return;
    
    // TODO: Implement find next
    console.log('Find next');
  };
  
  const handleFindPrevious = () => {
    if (!shouldHandleCommand(context, 'outliner:find-previous')) return;
    
    // TODO: Implement find previous
    console.log('Find previous');
  };
  
  const handleFilter = () => {
    if (!shouldHandleCommand(context, 'outliner:filter')) return;
    
    if (context.showFilter) {
      context.showFilter();
    } else {
      console.warn('Filter not implemented');
    }
  };
  
  const handleDeselect = () => {
    if (!shouldHandleCommand(context, 'outliner:deselect')) return;
    
    const { currentNodeId } = context;
    if (!currentNodeId) return;
    
    // Deselect any selected item
    context.selectr.clearSelection();
  };
  
  // ========================================
  // RETURN ALL HANDLERS
  // ========================================
  
  return {
    // Navigation
    handleNavigateUp,
    handleNavigateDown,
    handleNavigateLeft,
    handleNavigateRight,
    handleNavigateHome,
    handleNavigateEnd,
    handleNavigatePageUp,
    handleNavigatePageDown,
    
    // Selection
    handleSelectUp,
    handleSelectDown,
    handleSelectAll,
    handleSelectNone,
    handleSelectChildren,
    handleSelectSiblings,
    handleSelectDescendants,
    handleToggleSelection,
    
    // Editing
    handleAddAfter,
    handleAddBefore,
    handleAddChild,
    handleEdit,
    handleCancelEdit,
    handleDelete,
    handleDuplicate,
    
    // Movement
    handleMoveUp,
    handleMoveDown,
    handleIndent,
    handleOutdent,
    handleMoveToParent,
    
    // Clipboard
    handleCopy,
    handleCopyAsTabIndented,
    handleCopyAsSpaceIndented,
    handleCopyAsMarkdown,
    handleCopyAsNumbered,
    handleCopyAsOutline,
    handleCopyAsJson,
    handleCut,
    handlePaste,
    handlePasteAsChild,
    
    // Undo/Redo
    handleUndo,
    handleRedo,
    
    // View
    handleExpand,
    handleCollapse,
    handleToggleExpand,
    handleExpandAll,
    handleCollapseAll,
    handleExpandGeneration,
    
    // Search
    handleFind,
    handleFindNext,
    handleFindPrevious,
    handleFilter,
    handleDeselect
  };
};
